/**
 * useJobCompletionHandler - Centralized handler for job completion events
 *
 * This hook subscribes to the Zustand store and watches for jobs that transition
 * to terminal states (completed, failed, cancelled). When a job completes, it:
 *
 * 1. Shows appropriate toast notifications based on job type and status
 * 2. Triggers React Query cache invalidation based on entity type
 * 3. Handles special actions (e.g., update caption generation store)
 *
 * This replaces the scattered toast/refetch logic that was in useRealtimeSync
 * and various component-level handlers.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '@/core/store/useAppStore';
import { JobProgressState } from '@/core/store/slices/realtimeSlice';
import { isTerminalStatus, JobType, EntityType } from '@/services/jobsService';
import { postCacheUtils } from '@/domains/PostCreator/hooks/usePostsQuery';
import { mediaCacheUtils } from '@/core/MediaLibrary/hooks/useMediaQuery';
import { useCaptionGenerationStore } from '@/domains/PostCreator/hooks/useCaptionGenerationStore';

// Toast messages for job completion by type
const TOAST_CONFIG: Record<JobType, { success: string; failed: string }> = {
  media_share: {
    success: 'Share link ready',
    failed: 'Share creation failed',
  },
  image_analysis: {
    success: 'Image analysis complete',
    failed: 'Image analysis failed',
  },
  media_download: {
    success: 'Media downloaded',
    failed: 'Media download failed',
  },
  image_processing: {
    success: 'Image processed',
    failed: 'Image processing failed',
  },
  video_processing: {
    success: 'Video processed',
    failed: 'Video processing failed',
  },
  post_preparation: {
    success: 'Post prepared',
    failed: 'Post preparation failed',
  },
  social_publish: {
    success: 'Post published',
    failed: 'Post publishing failed',
  },
  caption_generation: {
    success: 'Caption generated',
    failed: 'Caption generation failed',
  },
  text_generation: {
    // This will be customized with result_data.generationType
    success: 'Generation complete',
    failed: 'Generation failed',
  },
  social_sync: {
    success: 'Social data synced',
    failed: 'Social sync failed',
  },
};

// Jobs that should not show toasts (silent background operations)
const SILENT_JOB_TYPES: JobType[] = ['image_processing', 'video_processing'];

interface UseJobCompletionHandlerOptions {
  clientId: string | undefined;
  enabled?: boolean;
}

export function useJobCompletionHandler({
  clientId,
  enabled = true,
}: UseJobCompletionHandlerOptions) {
  // Track which jobs we've already handled to avoid duplicate toasts
  const handledJobsRef = useRef<Set<string>>(new Set());

  // Store actions - use getState() to avoid subscribing to the store
  const storeActions = useRef({
    addToast: useAppStore.getState().addToast,
  });

  // Track previous jobs for comparison
  const prevJobsRef = useRef<Record<string, JobProgressState>>({});

  useEffect(() => {
    if (!enabled || !clientId) {
      return;
    }

    // Subscribe to store changes
    const unsubscribe = useAppStore.subscribe((state) => {
      const jobs = state.realtime.jobs;
      const prevJobs = prevJobsRef.current;

      // Check for jobs that have transitioned to terminal state
      for (const [jobId, job] of Object.entries(jobs)) {
        // Skip if we've already handled this job
        if (handledJobsRef.current.has(jobId)) {
          continue;
        }

        // Skip if job isn't in terminal state
        if (!isTerminalStatus(job.status)) {
          continue;
        }

        // Check if this job just transitioned (wasn't terminal before)
        const prevJob = prevJobs[jobId];
        if (prevJob && isTerminalStatus(prevJob.status)) {
          // Was already terminal, skip
          continue;
        }

        // Mark as handled
        handledJobsRef.current.add(jobId);

        // Handle the job completion
        handleJobCompletion(job, clientId);
      }

      // Clean up handled jobs that are no longer in the store
      Array.from(handledJobsRef.current).forEach((jobId) => {
        if (!jobs[jobId]) {
          handledJobsRef.current.delete(jobId);
        }
      });

      // Update previous jobs reference
      prevJobsRef.current = { ...jobs };
    });

    return () => {
      unsubscribe();
    };
  }, [clientId, enabled]);

  const handleJobCompletion = (job: JobProgressState, currentClientId: string) => {
    const jobType = job.jobType as JobType;
    const entityType = job.entityType as EntityType;

    // Skip toasts for silent job types (they show progress via other UI)
    const showToast = !SILENT_JOB_TYPES.includes(jobType);

    if (job.status === 'completed') {
      // Handle success
      handleJobSuccess(job, jobType, entityType, currentClientId, showToast);
    } else if (job.status === 'failed') {
      // Handle failure
      handleJobFailure(job, jobType, showToast);
    } else if (job.status === 'cancelled') {
      // Handle cancellation (usually no toast needed)
      console.log(`Job ${job.id} was cancelled`);
    }
  };

  const handleJobSuccess = (
    job: JobProgressState,
    jobType: JobType,
    entityType: EntityType,
    currentClientId: string,
    showToast: boolean
  ) => {
    // Get toast message
    let toastMessage = TOAST_CONFIG[jobType]?.success || 'Operation completed';

    // Special handling for text_generation - customize message based on generation_type
    if (jobType === 'text_generation' && job.resultData) {
      const generationType = job.resultData.generationType as string;

      switch (generationType) {
        case 'caption': {
          const variantsCount = job.resultData.variantsCount as number;
          toastMessage = variantsCount
            ? `Generated ${variantsCount} caption variants`
            : 'Captions generated';

          // Update the caption generation store with the results
          const postId = job.resultData.postId as string;
          const captions = job.resultData.captions as Array<{
            id: string;
            text: string;
            characterCount: number;
          }> | undefined;
          const altText = job.resultData.altText as string | undefined;

          if (postId && captions) {
            const { completeGeneration } = useCaptionGenerationStore.getState();
            completeGeneration(postId, {
              captions: captions.map((c) => ({
                id: c.id,
                text: c.text,
                characterCount: c.characterCount,
              })),
              altText: altText || '',
              generationId: job.resultData.generationId as number | undefined,
            });
          }
          break;
        }
        case 'brief_distillation':
          toastMessage = 'Brief context distilled';
          break;
        case 'idea_generation': {
          const ideasCount = job.resultData.ideasCount as number;
          toastMessage = ideasCount
            ? `Generated ${ideasCount} content ideas`
            : 'Ideas generated';
          break;
        }
        case 'pulse':
          toastMessage = 'Pulse generated';
          break;
        default:
          toastMessage = 'Generation complete';
      }

      console.log('Text generation completed:', {
        generationType,
        resultData: job.resultData,
      });
    }

    // Show toast
    if (showToast) {
      storeActions.current.addToast({
        message: toastMessage,
        type: 'success',
        duration: 5000,
      });
    }

    // Trigger cache invalidation based on entity type
    handleCacheInvalidation(entityType, currentClientId, jobType);
  };

  const handleJobFailure = (
    job: JobProgressState,
    jobType: JobType,
    showToast: boolean
  ) => {
    const errorMessage = job.errorMessage || TOAST_CONFIG[jobType]?.failed || 'Operation failed';

    // For text_generation caption failures, update the caption generation store
    if (jobType === 'text_generation' && job.resultData?.generationType === 'caption') {
      const postId = job.entityId;
      if (postId) {
        const { failGeneration } = useCaptionGenerationStore.getState();
        failGeneration(postId, errorMessage);
      }
    }

    if (showToast) {
      storeActions.current.addToast({
        message: errorMessage,
        type: 'error',
        duration: 7000,
      });
    }

    console.error(`Job ${job.id} (${jobType}) failed:`, errorMessage);
  };

  const handleCacheInvalidation = (
    entityType: EntityType,
    currentClientId: string,
    jobType: JobType
  ) => {
    switch (entityType) {
      case 'media_item':
        mediaCacheUtils.invalidateAllMedia(currentClientId);
        break;

      case 'post':
        postCacheUtils.invalidateAllPosts(currentClientId);
        break;

      case 'batch':
        break;

      case 'media_share':
        // No refetch needed for media share - it's a one-time operation
        break;

      case 'scheduled_post':
        postCacheUtils.invalidateAllPosts(currentClientId);
        break;

      default:
        // No cache invalidation needed
        break;
    }
  };
}
